import { Component, signal, viewChild, inject, OnInit, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMap, MapAdvancedMarker, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { FormsModule } from '@angular/forms';
import { SalesAgent, SalesAgentDto } from '../../../../features/sales/models/sales-agent.model';
import { AdminService } from '../../services/admin.service';

// Deck.gl Integrations
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { PhonePipe } from '../../../pipes/phone-number.pipe';

export type GeocodedAgent = SalesAgentDto & {
  position: google.maps.LatLngLiteral;
  fullAddress: string;
};

@Component({
  selector: 'app-agent-state-map',
  standalone: true,
  imports: [
    CommonModule, GoogleMap, MapAdvancedMarker, MapMarker, MapInfoWindow, FormsModule, PhonePipe
],
  templateUrl: './agent-state-map.component.html',
  styleUrl: './agent-state-map.component.scss'
})
export class AgentStateMapComponent implements OnInit, OnDestroy {
  adminService = inject(AdminService);
  googleMap = viewChild(GoogleMap);
  infoWindow = viewChild(MapInfoWindow);

  salesAgents = signal<SalesAgent[]>([]);
  geocodedAgents = signal<GeocodedAgent[]>([]);
  selectedAgent = signal<SalesAgentDto | null>(null);

  showHeatmap = signal<boolean>(false);
  showMarkers = signal<boolean>(true);

  private map?: google.maps.Map;
  private deckOverlay?: GoogleMapsOverlay;

  mapOptions: google.maps.MapOptions = {
    center: { lat: 37.0902, lng: -95.7129 },
    zoom: 4.5,
    maxZoom: 14,
    minZoom: 3,
    mapId: 'cf6545cdfd9ed9fe82ad5188',
    disableDefaultUI: true,
    gestureHandling: 'greedy',
    scrollwheel: true,
    zoomControl: true,
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#8599c0' }] },
      { elementType: 'geometry.fill', stylers: [{ color: '#b5c1d9' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#8dbfce' }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#5ba6bb' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#9dadcc' }] },
      { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#8e4b0d' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#0d508e' }] },
      { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#8e4b0d' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#5373a7' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff', weight: 0.01 }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#a5cbd8', visibility: 'off' }] },
      { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#3d99b1' }] },
      { featureType: "poi", stylers: [{ visibility: "off" }] },
      { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#37619a' }] },
      { featureType: 'administrative.locality', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff', weight: 0.01 }] }
    ]
  };

  constructor() {
    effect(() => {
      const mapInstance = this.googleMap()?.googleMap;
      const shouldShow = this.showHeatmap();
      const agents = this.geocodedAgents();

      // 1. Initialize and mount the deck.gl overlay ONLY ONCE
      if (mapInstance && !this.map) {
        this.map = mapInstance;
        
        // Use interleaved: false to isolate the WebGL canvas and prevent bindings from dropping
        this.deckOverlay = new GoogleMapsOverlay({ 
          interleaved: false,
          layers: [] 
        });
        
        // Mount permanently
        this.deckOverlay.setMap(this.map);
      }

      if (!this.map || !this.deckOverlay) return;

      // 2. Continually update the layer props instead of unmounting
      this.updateDeckGlHeatmap(agents, shouldShow);
    });
  }

  ngOnInit() {
    this.loadAgents();
  }

  ngOnDestroy() {
    // Gracefully clean up the permanent overlay when the component dies
    if (this.deckOverlay) {
      this.deckOverlay.setMap(null);
      this.deckOverlay.finalize();
    }
  }

  // ==================== DECK.GL HEATMAP ====================
  private updateDeckGlHeatmap(agents: GeocodedAgent[], isVisible: boolean) {
    const layer = new HeatmapLayer({
      id: 'sales-heatmap-layer',
      data: agents,
      visible: isVisible, // Reactively toggle visibility here without destroying WebGL context
      getPosition: (d: GeocodedAgent) => [d.position.lng, d.position.lat],
      getWeight: () => 1,
      radiusPixels: 45, 
      intensity: 1.5,
      threshold: 0.03,
      colorRange: [
        [0, 255, 204, 0],      
        [0, 255, 204, 80],     
        [0, 179, 255, 160],    
        [255, 93, 92, 255]     
      ]
    });

    this.deckOverlay?.setProps({ layers: [layer] });
  }

  // ==================== DATA ====================
  loadAgents() {
    this.adminService.getSalesAgents().subscribe(agents => {
      const validAgents = agents.filter(agent => agent.city && agent.state);
      this.salesAgents.set(validAgents);
      console.log('validAgents', validAgents);
      this.geocodeAgentAddresses(validAgents);
    });
  }

  private async geocodeAgentAddresses(agents: SalesAgent[]) {
    const geocoder = new google.maps.Geocoder();

    const promises = agents.map(agent => {
      const address = `${agent.street || ''}, ${agent.city}, ${agent.state} ${agent.zipCode || ''}`;
      return new Promise<GeocodedAgent | null>(resolve => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results?.[0]) {
            const loc = results[0].geometry.location;
            
            // Use the spread operator (...agent) to automatically keep all 
            // original API properties, including the phone number.
            resolve({
              ...agent, 
              id: agent.agentId?.toString() || agent.userId,
              position: { lat: loc.lat(), lng: loc.lng() },
              fullAddress: results[0].formatted_address
            } as GeocodedAgent);
            
          } else {
            resolve(null);
          }
        });
      });
    });

    const results = await Promise.all(promises);
    this.geocodedAgents.set(results.filter((a): a is GeocodedAgent => a !== null));
  }

  // ==================== EVENTS ====================
  onMapInitialized() {}

  onMarkerClick(markerComponent: MapMarker, agent: GeocodedAgent) {
    this.selectedAgent.set(agent);
    this.infoWindow()?.open(markerComponent);
  }

  getMarkerOptions(agent: GeocodedAgent): google.maps.MarkerOptions {
    return {
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: '#00ffcc',
        fillOpacity: 1.0,
        strokeColor: '#0b0f19',
        strokeWeight: 2
      }
    };
  }
}